import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  LinearScale,
  Legend,
  CategoryScale,
  PointElement,
  LineElement,
  Title,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(LinearScale, Legend, CategoryScale, PointElement, LineElement, Title, Tooltip);

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const DashboardLineChart = () => {
  const [events, setEvents] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [yearFilter, setYearFilter] = useState("last12");
  const [yearOptions, setYearOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isLast12 = yearFilter === "last12";

  const getFilterRange = () => {
    const now = new Date();
    if (isLast12) {
      const to = new Date();
      const from = new Date(to);
      from.setMonth(to.getMonth() - 11);
      from.setDate(1);
      return { from, to };
    } else {
      const from = new Date(`${yearFilter}-01-01`);
      const to = new Date(`${yearFilter}-12-31`);
      return { from, to };
    }
  };

  const generateLast12MonthLabels = () => {
    const now = new Date();
    const endMonth = now.getMonth();
    const labels = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(endMonth - i);
      labels.push(monthNames[date.getMonth()]);
    }
    return labels;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/business/events`, { withCredentials: true });
        const fetchedEvents = res?.data.data || [];
        setEvents(fetchedEvents);

        // Extract all unique years
        const yearsSet = new Set();
        fetchedEvents.forEach((event) => {
          const year = new Date(event.createdAt).getFullYear();
          yearsSet.add(year);
        });

        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setYearOptions(["last12", ...sortedYears.map(String)]);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const { from, to } = getFilterRange();
    const tempRevenue = new Array(12).fill(0);

    events.forEach(event => {
      const eventDate = new Date(event.createdAt);
      if (eventDate >= from && eventDate <= to) {
        const value = event.expPaymentAmount || 0;

        if (isLast12) {
          const diffInMonths =
            (eventDate.getFullYear() - from.getFullYear()) * 12 +
            eventDate.getMonth() - from.getMonth();
          if (diffInMonths >= 0 && diffInMonths < 12) {
            tempRevenue[diffInMonths] += value;
          }
        } else {
          tempRevenue[eventDate.getMonth()] += value;
        }
      }
    });

    const labels = isLast12 ? generateLast12MonthLabels() : monthNames;

    setChartData({
      labels,
      datasets: [
        {
          label: "Revenue",
          data: tempRevenue,
          fill: false,
          borderColor: "#21A62C",
          tension: 0.2,
        },
      ],
    });
  }, [yearFilter, events]);

  const chartOptions = {
    plugins: {
      title: {
      display: false, 
    },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: { display: true },
        ticks: {
          font: { family: 'Poppins', size: 15 },
          padding: 15,
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { family: 'Poppins', size: 11 },
        },
        title: {
          display: true,
          text: 'Amount',
          font: {
            family: 'Poppins',
            size: 11,
            weight: 'bold',
          },
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="dashboard-line-chart" style={{
      height: '100%',
      backgroundColor: '#FFFFFF',
      paddingLeft: '0',
      paddingTop: '1%',
      paddingRight: '0',
      fontFamily: 'Poppins',
      boxShadow: '0px 2px 4px #15223214',
    }}>
      {/* Header and Dropdown */}
      <div className="dashboard-line-chart-inner">
      <div className="dashboard-line-chart-heading"  style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1.5rem",
      }}>
        <h2 style={{
          color: "#1C1154",
          fontFamily: "Poppins",
          fontSize: "24px",
          fontWeight: "500",
          margin: 0,
        }}>
          Revenue
        </h2>

        <div style={{ position: "relative", width: "160px" }}>
          <div
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              backgroundColor: "#fff",
              padding: "10px 14px",
              paddingRight: "36px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "14px",
              color: "#625C73",
              userSelect: "none",
            }}
          >
            {yearFilter === "last12" ? "Last 12 Months" : `Year ${yearFilter}`}
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              style={{
                position: "absolute",
                right: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="#625C73"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {dropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "105%",
                left: 0,
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                zIndex: 10,
                padding: "6px 0",
              }}
            >
              {yearOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    setYearFilter(option);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#625C73",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#F3F3F3"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {option === "last12" ? "Last 12 Months" : `Year ${option}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {chartData ? (
        <Line data={chartData} options={chartOptions} />
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
    </div>
  );
};

export default DashboardLineChart;
